import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { getReelFeed } from '../api/reel';
import styles from '../styles/ReelPopup.module.css';

const ReelPopup = ({ reelId, onClose }) => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch product reels on mount
  useEffect(() => {
    const fetchReels = async () => {
      try {
        setIsLoading(true);
        const data = await getReelFeed(1, 'product');
        if (data.reels && data.reels.length > 0) {
          setReels(data.reels);
          // If reelId is provided, set currentIndex to that reel
          if (reelId) {
            const idx = data.reels.findIndex(r => r._id === reelId);
            if (idx !== -1) setCurrentIndex(idx);
          }
        }
      } catch (err) {
        console.error('Failed to fetch reels for popup:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReels();
  }, [reelId]);

  // Auto-advance to next reel when current one ends
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      if (reels.length > 1) {
        setCurrentIndex((prev) => (prev + 1) % reels.length);
      }
    };

    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [reels]);

  // Autoplay video when reel changes
  useEffect(() => {
    if (videoRef.current && reels.length > 0) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked, that's okay
      });
    }
  }, [currentIndex, reels]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    if (onClose) onClose();
  };

  const handleReelClick = () => {
    const currentReel = reels[currentIndex];
    if (currentReel && currentReel._id) {
      // Navigate to /reels page with the specific reel ID in state
      navigate('/reels', { state: { scrollToReelId: currentReel._id } });
    }
  };

  // Don't render if closed or no reels available
  if (!isVisible || isLoading || reels.length === 0) {
    return null;
  }

  const currentReel = reels[currentIndex];

  return (
    <div className={styles.popupContainer}>
      {/* Close button */}
      <button
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="Close reel popup"
      >
        <IoClose size={18} />
      </button>

      {/* Video container - clickable to navigate */}
      <div className={styles.videoContainer} onClick={handleReelClick}>
        <video
          ref={videoRef}
          src={currentReel.videoUrl}
          className={styles.video}
          muted
          loop={reels.length === 1}
          playsInline
          autoPlay
        />
      </div>
    </div>
  );
};

export default ReelPopup;
